"use client"

import { forwardRef, useCallback, useImperativeHandle, useRef } from "react"
import { Loader2 } from "lucide-react"

import { DataImportApiProvider } from "@/hooks/api/data-import-api/context"
import { selfServeDataImportApi } from "@/hooks/api/data-import-api/self-serve"
import {
  ReviewStep as ConfigurationReviewStep,
  ReviewStepHandle,
  ReviewStepStatus,
} from "@/components/data-imports/configuration/review-step"

interface ReviewStepProps {
  dataImportId: string
  onStatusChange?: (status: ReviewStepStatus) => void
}

export const ReviewStep = forwardRef<ReviewStepHandle, ReviewStepProps>(function ReviewStep(
  { dataImportId, onStatusChange },
  ref
) {
  return (
    <DataImportApiProvider api={selfServeDataImportApi}>
      <ReviewStepInner ref={ref} dataImportId={dataImportId} onStatusChange={onStatusChange} />
    </DataImportApiProvider>
  )
})

const ReviewStepInner = forwardRef<ReviewStepHandle, ReviewStepProps>(function ReviewStepInner(
  { dataImportId, onStatusChange },
  ref
) {
  const api = selfServeDataImportApi
  const { data, isLoading } = api.useFetchDataImport(dataImportId)
  const reviewRef = useRef<ReviewStepHandle>(null)

  useImperativeHandle(
    ref,
    () => ({
      startImport: () => reviewRef.current?.startImport() ?? Promise.resolve(),
      saveChanges: () => reviewRef.current?.saveChanges() ?? Promise.resolve(),
    }),
    []
  )

  const handleStatusChange = useCallback(
    (status: ReviewStepStatus) => {
      onStatusChange?.(status)
    },
    [onStatusChange]
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="text-muted-foreground animate-spin" />
      </div>
    )
  }

  if (!data?.data_import) {
    return <p className="text-muted-foreground py-12 text-center text-sm">Data import not found.</p>
  }

  return (
    <div className="h-full overflow-y-auto pt-12 pb-6">
      <ConfigurationReviewStep ref={reviewRef} dataImport={data.data_import} onStatusChange={handleStatusChange} />
    </div>
  )
})

export type { ReviewStepHandle, ReviewStepStatus }
